import { playgroundRunAll } from './steps/aiConfigs/playgroundRunAll';
import {
  individualTargetRule as individualTargetRulebaseIndividualTargetRule,
  useIndividualTargetRule,
} from './steps/featureFlags/individualTargetRule';
import type { OnboardingGuidanceFlow, OnboardingGuidanceFlowWithHandler } from './types';

/**
 * Central registry of all onboarding flows.
 * This is the single source of truth for what flows exist.
 * Add new flows here as they are created.
 * Flows in this registry do not have onClick handlers - use useFlowRegistry() hook to get flows with handlers.
 */
export const flowRegistry: Record<string, OnboardingGuidanceFlow> = {
  individualTargetRule: individualTargetRulebaseIndividualTargetRule,
  playgroundRunAll,
  // Add new flows here as they are created:
  // experimentation flows:
  // observability flows:
};

/**
 * Mapping of flow keys to their hook functions.
 * Only flows that need dynamic onClick handlers should be registered here.
 * This is the single source of truth for which flows have hooks.
 *
 * To add a new flow with a handler:
 * 1. Add the flow to flowRegistry (defines it exists)
 * 2. Add the hook to this mapping - it will be called automatically
 */
const flowHooks = {
  individualTargetRule: useIndividualTargetRule,
  // Add new flow hooks here as they are created:
  // experimentationFlow: useExperimentationFlow,
  // observabilityFlow: useObservabilityFlow,
} as const;

type FlowHookKey = keyof typeof flowHooks;

/**
 * Hook that returns all onboarding flows with their handlers configured.
 * Automatically iterates over flowRegistry and enhances flows that have corresponding hooks.
 *
 * To add a new flow with a handler:
 * 1. Add the flow to flowRegistry (defines it exists)
 * 2. Add the hook to flowHooks mapping above - it will be called automatically
 */
export function useFlowRegistry(onClose?: () => void) {
  // Call each flow hook at the top level (React rules of hooks - no hooks inside loops)
  const individualTargetRuleResult = useIndividualTargetRule(onClose);

  const hookResults: Partial<Record<FlowHookKey, ReturnType<(typeof flowHooks)[FlowHookKey]>>> = {
    individualTargetRule: individualTargetRuleResult,
    // When adding a new flow with a hook, add: useMyNewFlow(onClose), and add to hookResults
  };

  // Build flows array by iterating over flowRegistry (single source of truth)
  // Return type is OnboardingGuidanceFlow | OnboardingGuidanceFlowWithHandler
  const flows: Array<OnboardingGuidanceFlow | OnboardingGuidanceFlowWithHandler> = Object.keys(flowRegistry).map(
    (flowKey) => {
      // If this flow has a hook, use the hook result (OnboardingGuidanceFlowWithHandler); otherwise use static flow (OnboardingGuidanceFlow)
      const hookResult = hookResults[flowKey as FlowHookKey];
      return hookResult?.flow ?? flowRegistry[flowKey];
    },
  );

  // Collect first error encountered (in practice, typically only one flow has error state)
  const error = Object.values(hookResults).find((result) => result?.error)?.error || undefined;

  // Build a map of flow names to their isCreating state
  // This allows the UI to disable specific flow buttons during creation
  const isCreatingMap = Object.keys(flowRegistry).reduce<Record<string, boolean>>((acc, flowKey) => {
    const hookResult = hookResults[flowKey as FlowHookKey];
    if (hookResult?.isCreating !== undefined) {
      // Use the flow name as the key (from the actual flow object, not the registry key)
      return { ...acc, [hookResult.flow.name]: hookResult.isCreating };
    }
    return acc;
  }, {});

  return {
    flows,
    error,
    isCreatingMap,
  };
}
